import React, { useEffect, useState, useRef } from "react";
import apiClient from "../utils/apiClient";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.min.css";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faEllipsisV,
  faEye,
  faCancel,
  faPlus,
  faPencil,
  faCheckCircle,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import ApproverModal from "./ApproverModal";
import Swal from "sweetalert2";

const DeliveryChallanList = ({ permissions }) => {
  const [personOptions, setPersonOptions] = useState([]);
  const [challans, setChallans] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [selectedChallanId, setSelectedChallanId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const name = localStorage.getItem("name");
  const badgeId = localStorage.getItem("badgeId");
  const pin = localStorage.getItem("pin");
  const code = localStorage.getItem("code");
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [selectedPrefix, setSelectedPrefix] = useState("All");
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [selectedPreparedBy, setSelectedPreparedBy] = useState("All");

  // Filter challans
  const filteredChallans = challans.filter((challan) => {
    // Prefix filter
    if (selectedPrefix !== "All" && challan.prefix !== selectedPrefix)
      return false;

    // Prepared By filter
    if (
      selectedPreparedBy !== "All" &&
      challan.createdBy !== selectedPreparedBy
    )
      return false;

    // Department filter
    if (selectedDepartment !== "All") {
      const departments = (challan.itemDetails || []).flatMap((item) =>
        (item.locationDetails || []).map((l) => l.department),
      );

      if (!departments.includes(selectedDepartment)) return false;
    }

    const searchLower = searchTerm.toLowerCase();
    return (
      `${challan.prefix}${challan.refNo}`.toLowerCase().includes(searchLower) ||
      challan.date.toLowerCase().includes(searchLower) ||
      (challan.ledger || "").toLowerCase().includes(searchLower) ||
      (challan.name || "").toLowerCase().includes(searchLower) ||
      (challan.mobileNo || "").toLowerCase().includes(searchLower) ||
      (challan.onAccount || "").toLowerCase().includes(searchLower) ||
      (challan.isApproved || "").toLowerCase().includes(searchLower) ||
      (challan.permitBy || "").toLowerCase().includes(searchLower)
    );
  });

  // --- Unique Prefixes ---
  const uniquePrefixes = [
    "All",
    ...Array.from(new Set(challans.map((c) => c.prefix))).filter((p) => p),
  ];
  // --- Unique Departments ---
  const uniqueDepartments = [
    "All",
    ...Array.from(
      new Set(
        challans.flatMap((c) =>
          (c.itemDetails || []).flatMap((item) =>
            (item.locationDetails || []).map((l) => l.department),
          ),
        ),
      ),
    ).filter(Boolean),
  ];

  // --- Unique Prepared By ---
  const uniquePreparedBy = [
    "All",
    ...Array.from(new Set(challans.map((c) => c.createdBy))).filter(Boolean),
  ];

  // --- Group challans ---
  const groupChallans = () => {
    const visited = new Set();
    const groups = [];
    filteredChallans.forEach((challan) => {
      if (visited.has(challan.id)) return;
      if (challan.relatedTranshipId) {
        const pair = filteredChallans.find(
          (c) => c.id == challan.relatedTranshipId,
        );
        if (pair) {
          groups.push([challan, pair]);
          visited.add(challan.id);
          visited.add(pair.id);
        } else {
          groups.push([challan]);
          visited.add(challan.id);
        }
      } else {
        groups.push([challan]);
        visited.add(challan.id);
      }
    });
    return groups;
  };

  const groupedChallans = groupChallans();

  // Pagination
  const totalPages = Math.ceil(groupedChallans.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedGroups = groupedChallans.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Counts
  const pendingCount = challans.filter((c) => c.isApproved == "Pending").length;
  const approvedCount = challans.filter(
    (c) => c.isApproved == "Approved",
  ).length;
  const cancelledCount = challans.filter(
    (c) => c.isApproved == "Cancelled",
  ).length;
  const TotalChallan = filteredChallans.length;

  useEffect(() => {
    fetchDeliveryChallans();
    fetchPersons();
  }, []);

  const fetchDeliveryChallans = async () => {
    try {
      const response = await apiClient.get(
        `${SERVER_BASE}/getDeliveryChallans`,
        {
          params: { code },
        },
      );

      // Filter only those with isTesting === 1
      const filteredChallans = response.data.filter(
        (challan) => challan.isTesting != "1",
      );

      setChallans(filteredChallans);
    } catch (error) {
      console.error("Error fetching delivery challans:", error);
    }
  };

  const fetchPersons = async () => {
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getApprovers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPersonOptions(
        response.data.map((p) => ({
          value: p.id,
          mobileNo: p.contactNo,
          badgeId: p.badgeId,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const removeDuplicateItems = (items) => {
    return [
      ...new Map(
        items.map((i) => [
          `${i.itemName}|${i.quantity}|${i.uom}|${i.remarks}`,
          i,
        ]),
      ).values(),
    ];
  };

  // --- Single-row handlers ---
  const handleEdit = (challan) => {
    const rowData = { ...challan };
    if (Array.isArray(rowData.itemDetails))
      rowData.itemDetails = removeDuplicateItems(rowData.itemDetails);
    // navigate("../addDeliveryChallan", { state: { rowData } });
    rowData?.challanType == "Negative"
      ? navigate("../DeliveryChallanNegative", { state: { rowData } })
      : navigate("../addDeliveryChallan", { state: { rowData } });
  };
  const handleClone = (challan) => {
    const rowData = { ...challan };
    if (Array.isArray(rowData.itemDetails))
      rowData.itemDetails = removeDuplicateItems(rowData.itemDetails);
    // navigate("../addDeliveryChallan", {
    //   state: { rowData, isClone: true },
    // });
    rowData?.challanType == "Negative"
      ? navigate("../DeliveryChallanNegative", {
          state: { rowData, isClone: true },
        })
      : navigate("../addDeliveryChallan", {
          state: { rowData, isClone: true },
        });
  };
  const handleView = (challan) =>
    navigate("../../previewDeliveryChallan", { state: { id: challan.id } });

  const handleApproveCancel = async (rowData) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to cancel the Approved Challan? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel it!",
    }).then((res) => {
      if (res.isConfirmed) {
        apiClient
          .post(`${SERVER_BASE}/cancelChallan/${rowData.id}`, {
            refNo: rowData.refNo,
            prefix: rowData.prefix,
            itemDetails: rowData.itemDetails,
          })
          .then(fetchDeliveryChallans);
      }
    });
  };

  const handlePendingCancel = async (rowData) => {
    Swal.fire({
      title: "Confirm Cancellation",
      text: "Enter your PIN to cancel this voucher. This action cannot be undone.",
      icon: "warning",
      input: "password",
      inputPlaceholder: "Enter PIN",
      inputAttributes: {
        maxlength: 6,
        autocapitalize: "off",
        autocorrect: "off",
      },
      showCancelButton: true,
      confirmButtonText: "Cancel Voucher",
      preConfirm: async (enteredPin) => {
        if (!enteredPin) {
          Swal.showValidationMessage("PIN is required");
          return false;
        }

        if (enteredPin !== pin) {
          Swal.showValidationMessage("Invalid PIN");
          return false;
        }

        try {
          await apiClient.post(
            `${SERVER_BASE}/cancelPendingChallan/${rowData.id}`,
            {
              refNo: rowData.refNo,
              prefix: rowData.prefix,
              itemDetails: rowData.itemDetails,
              cancelledBy: name, // ✅ Name added
            },
          );

          return true; // ✅ tells Swal it's successful
        } catch (err) {
          Swal.showValidationMessage("Failed to cancel voucher");
          return false;
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: "success",
          title: "Cancelled",
          text: "Voucher cancelled successfully.",
        });

        fetchDeliveryChallans();
      }
    });
  };
  const hasAutoCancelled = React.useRef(false);
  useEffect(() => {
    const autoCancelPendingChallans = async () => {
      if (hasAutoCancelled.current || challans.length === 0) return;
      hasAutoCancelled.current = true;
      const today = new Date().toISOString().split("T")[0];
      const pendingChallans = challans.filter(
        (challan) => challan.isApproved == "Pending" && challan.date < today,
      );
      if (pendingChallans.length == 0) return;
      for (const challan of pendingChallans) {
        try {
          await apiClient.post(
            `${SERVER_BASE}/cancelPendingChallan/${challan.id}`,
            {
              cancelledBy: "Auto Cancelled", // ✅ Name added
            },
          );
        } catch (error) {
          console.error(
            `Error auto-cancelling challan ID ${challan.id}:`,
            error,
          );
        }
      }
      // Refresh challans after auto-cancellation
      fetchDeliveryChallans();
    };

    autoCancelPendingChallans();
  }, [challans]);

  // --- Group handlers ---
  const handleEditGroup = (group) => handleEdit(group[0]);
  const handleViewGroup = (group) => handleView(group[0]);

  const handleApproveCancelGroup = (group) => {
    const groups = Array.isArray(group) ? group : [group];
    groups.forEach(handleApproveCancel);
  };

  const handlePendingCancelGroup = (group) => {
    const groups = Array.isArray(group) ? group : [group];
    groups.forEach(handlePendingCancel);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedPrefix, selectedDepartment, selectedPreparedBy]);

  return (
    <div className="formcontainer mt-5">
      {permissions?.read && (
        <>
          <div className="d-flex justify-content-between">
            <h3>Delivery Challan</h3>
            <p className="text-dark">Count : {TotalChallan}</p>
          </div>
          <div className="d-flex justify-content-between">
            {permissions?.write && (
              <button
                className="btn btn-outline-success"
                onClick={() => navigate("../addDeliveryChallan")}
              >
                Add Challan
              </button>
            )}

            {permissions?.write && (
              <button
                className="btn btn-outline-success"
                onClick={() => navigate("../DeliveryChallanNegative")}
              >
                Add Negative Challan
              </button>
            )}
            <input
              style={{ width: "18%" }}
              className="form-control"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {/* Prefix Filter */}
            <select
              className="form-select w-auto me-2"
              value={selectedPrefix}
              onChange={(e) => setSelectedPrefix(e.target.value)}
            >
              {uniquePrefixes.map((p) => (
                <option key={p} value={p}>
                  {p === "All" ? "All Prefixes" : p}
                </option>
              ))}
            </select>

            {/* Department Filter */}
            <select
              className="form-select w-auto me-2"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
            >
              {uniqueDepartments.map((d) => (
                <option key={d} value={d}>
                  {d === "All" ? "All Departments" : d}
                </option>
              ))}
            </select>

            {/* Prepared By Filter */}
            <select
              className="form-select w-auto me-2"
              value={selectedPreparedBy}
              onChange={(e) => setSelectedPreparedBy(e.target.value)}
            >
              {uniquePreparedBy.map((p) => (
                <option key={p} value={p}>
                  {p === "All" ? "All Prepared By" : p}
                </option>
              ))}
            </select>

            <select
              className="form-select w-auto"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              {[25, 50, 100, 200].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
          {/* <div className="d-flex  mt-2">
            <p className="text-success">Approved: {approvedCount}</p>
            <p className="text-warning">Pending: {pendingCount}</p>
            <p className="text-danger">Cancelled: {cancelledCount}</p>
            <p className="text-dark">Total Challan: {TotalChallan}</p>
          </div> */}
          <div className="mt-2  table-responsive">
            <table
              className="table table-bordered"
              style={{ fontSize: "14px", verticalAlign: "middle" }}
            >
              <thead>
                <tr>
                  <th>*</th>
                  <th style={{ width: "200px" }}>Ref No</th>
                  <th style={{ width: "200px" }}>Date</th>
                  <th style={{ width: "360px" }}>Receiver</th>
                  <th style={{ width: "110px" }}>Vehicle</th>
                  <th>Driver</th>
                  <th>Mobile</th>
                  <th>Account</th>
                  <th>Status</th>
                  <th>Department</th>
                  <th style={{ width: "210px" }}>Permitted By</th>
                  <th>Preview</th>
                  <th>#</th>
                </tr>
              </thead>
              <tbody>
                {paginatedGroups.map((group, gIndex) => {
                  if (group.length == 2) {
                    const [c1, c2] = group;
                    return (
                      <React.Fragment key={`group-${gIndex}`}>
                        <tr>
                          <td rowSpan={2}>{startIndex + gIndex + 1}</td>
                          <td>
                            {c1.prefix}
                            {c1.refNo}
                          </td>
                          <td rowSpan={2} style={{ verticalAlign: "middle" }}>
                            {moment(c1.date).format("DD-MMM-YY")}
                          </td>
                          <td>{c1.ledger}</td> {/* Receiver = ledger */}
                          <td rowSpan={2} style={{ verticalAlign: "middle" }}>
                            {c1.vehicleNo || "By Hand"}
                          </td>
                          <td rowSpan={2} style={{ verticalAlign: "middle" }}>
                            {c1.name}
                          </td>
                          <td rowSpan={2} style={{ verticalAlign: "middle" }}>
                            {c1.mobileNo}
                          </td>
                          <td rowSpan={2} style={{ verticalAlign: "middle" }}>
                            {c1.onAccount}
                          </td>
                          <td>
                            {c1.isApproved == "Approved" &&
                            c1.cancellationStatus == "Pending for Cancellation"
                              ? c1.cancellationStatus
                              : c1.isApproved}
                          </td>
                          <td rowSpan={2} style={{ verticalAlign: "middle" }}>
                            {[
                              ...new Set(
                                c1.itemDetails.flatMap((item) => {
                                  const locs = Array.isArray(
                                    item.locationDetails,
                                  )
                                    ? item.locationDetails
                                    : [];

                                  return locs.map((l) => l.department);
                                }),
                              ),
                            ].join(", ") || "-"}
                          </td>
                          <td rowSpan={2} style={{ verticalAlign: "middle" }}>
                            {c1.permitBy == "Committee Member"
                              ? c1.authorityName
                              : c1.permitBy == "Letter or Email from Beas"
                                ? c1.letterIssuerName
                                : c1.permitBy}
                          </td>
                          <td>
                            {c1.isApproved !== "Draft" && (
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => handleViewGroup(group)}
                              >
                                <FontAwesomeIcon icon={faEye} /> View
                              </button>
                            )}
                          </td>
                          <td rowSpan={2}>
                            <div className="dropdown">
                              <button
                                className="btn btn-light"
                                type="button"
                                data-bs-toggle="dropdown"
                              >
                                <FontAwesomeIcon icon={faEllipsisV} />
                              </button>
                              <ul className="dropdown-menu dropdown-menu-end">
                                {permissions?.edit &&
                                  (c1.isApproved == "Pending" ||
                                    c1.isApproved == "Draft") && (
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() => handleEditGroup(group)}
                                      >
                                        <FontAwesomeIcon icon={faEdit} /> Edit
                                      </button>
                                    </li>
                                  )}
                                {/* {permissions?.read &&
                                  c1.isApproved !== "Draft" && (
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() => handleViewGroup(group)}
                                      >
                                        <FontAwesomeIcon icon={faEye} /> Preview
                                      </button>
                                    </li>
                                  )} */}
                                {permissions?.delete &&
                                  c1.isApproved != "Cancelled" &&
                                  c1.isApproved != "Approved" && (
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() =>
                                          handlePendingCancelGroup(group)
                                        }
                                      >
                                        <FontAwesomeIcon icon={faCancel} />{" "}
                                        Cancel
                                      </button>
                                    </li>
                                  )}

                                <li>
                                  <hr />
                                </li>
                                <li className="dropdown-item text-muted">
                                  <FontAwesomeIcon icon={faPlus} />{" "}
                                  {c1.createdBy
                                    ? `${c1.createdBy} (${
                                        c1.createdAt
                                          ? moment(c1.createdAt).format(
                                              "DD-MMM-YY hh:mm A",
                                            )
                                          : ""
                                      }) `
                                    : "-"}
                                </li>
                                {c1.updatedBy ? (
                                  <li className="dropdown-item text-muted">
                                    <FontAwesomeIcon icon={faPencil} />{" "}
                                    {c1.updatedBy
                                      ? `${c1.updatedBy} (${
                                          c1.updatedAt
                                            ? moment(c1.updatedAt).format(
                                                "DD-MMM-YY hh:mm A",
                                              )
                                            : ""
                                        })`
                                      : ""}
                                  </li>
                                ) : null}
                                {c1.approvedBy ? (
                                  <li className="dropdown-item text-muted">
                                    {c1.approvedBy && (
                                      <>
                                        <FontAwesomeIcon icon={faCheckCircle} />{" "}
                                        {c1.approvedBy}
                                        {`${
                                          c1.approvedAt
                                            ? ` (${moment(c1.approvedAt).format(
                                                "DD-MMM-YY hh:mm A",
                                              )})`
                                            : ""
                                        }`}
                                      </>
                                    )}
                                  </li>
                                ) : null}
                              </ul>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            {c2.prefix} {c2.refNo}
                          </td>
                          <td>{c2.transporterName}</td>{" "}
                          {/* Receiver = transporter_name */}
                        </tr>
                      </React.Fragment>
                    );
                  }

                  const c = group[0];
                  return (
                    <tr key={c.id}>
                      <td>{startIndex + gIndex + 1}</td>
                      <td>
                        {c.prefix}
                        {c.refNo}
                      </td>
                      <td>{moment(c.date).format("DD-MMM-YY")}</td>
                      <td>{c.ledger}</td>
                      <td>{c.vehicleNo || "By Hand"}</td>
                      <td>{c.name}</td>
                      <td>{c.mobileNo}</td>
                      <td>{c.onAccount}</td>
                      <td>
                        {c.isApproved == "Approved" &&
                        c.cancellationStatus == "Pending for Cancellation"
                          ? c.cancellationStatus
                          : c.isApproved}
                      </td>
                      <td>
                        <td>
                          {[
                            ...new Set(
                              c.itemDetails.flatMap((item) => {
                                const locs = Array.isArray(item.locationDetails)
                                  ? item.locationDetails
                                  : [];

                                return locs.map((l) => l.department);
                              }),
                            ),
                          ].join(", ") || "-"}
                        </td>
                      </td>
                      <td>
                        {c.permitBy == "Committee Member"
                          ? c.authorityName
                          : c.permitBy == "Letter or Email from Beas"
                            ? c.letterIssuerName
                            : c.permitBy}
                      </td>
                      <td>
                        {" "}
                        {permissions?.read && c.isApproved != "Draft" && (
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleView(c)}
                          >
                            View
                          </button>
                        )}
                      </td>
                      <td>
                        <div className="dropdown">
                          <button
                            className="btn btn-light"
                            type="button"
                            data-bs-toggle="dropdown"
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => handleClone(c)}
                              >
                                <FontAwesomeIcon icon={faCopy} /> Clone
                              </button>
                            </li>
                            {permissions?.edit &&
                              (c.isApproved == "Pending" ||
                                c.isApproved == "Draft") && (
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => handleEdit(c)}
                                  >
                                    <FontAwesomeIcon icon={faEdit} /> Edit
                                  </button>
                                </li>
                              )}

                            {/* {permissions?.read && c.isApproved != "Draft" && (
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleView(c)}
                                >
                                  <FontAwesomeIcon icon={faEye} /> Preview
                                </button>
                              </li>
                            )} */}
                            {permissions?.delete &&
                              c.isApproved != "Cancelled" &&
                              c.isApproved != "Approved" && (
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => handlePendingCancelGroup(c)}
                                  >
                                    <FontAwesomeIcon icon={faCancel} /> Cancel
                                  </button>
                                </li>
                              )}
                            <li>
                              <hr />
                            </li>
                            <li className="dropdown-item text-muted">
                              <FontAwesomeIcon icon={faPlus} />{" "}
                              {c.createdBy
                                ? `${c.createdBy} (${
                                    c.createdAt
                                      ? moment(c.createdAt).format(
                                          "DD-MMM-YY hh:mm A",
                                        ) // 5*60 + 30 = 330
                                      : ""
                                  }) `
                                : "-"}
                            </li>
                            {c.updatedBy ? (
                              <li className="dropdown-item text-muted">
                                <FontAwesomeIcon icon={faPencil} />{" "}
                                {c.updatedBy
                                  ? `${c.updatedBy} (${
                                      c.updatedAt
                                        ? moment(c.updatedAt).format(
                                            "DD-MMM-YY hh:mm A",
                                          )
                                        : ""
                                    })`
                                  : ""}
                              </li>
                            ) : null}
                            {c.approvedBy ? (
                              <li className="dropdown-item text-muted">
                                {c.approvedBy && (
                                  <>
                                    <FontAwesomeIcon icon={faCheckCircle} />{" "}
                                    {c.approvedBy}
                                    {`${
                                      c.approvedAt
                                        ? ` (${moment(c.approvedAt).format(
                                            "DD-MMM-YY hh:mm A",
                                          )})`
                                        : ""
                                    }`}
                                  </>
                                )}
                              </li>
                            ) : null}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <button
              className="btn btn-secondary me-2"
              onClick={handlePreviousPage}
              disabled={currentPage == 1}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn btn-secondary"
              onClick={handleNextPage}
              disabled={currentPage == totalPages || totalPages == 0}
            >
              Next
            </button>
          </div>

          <ApproverModal
            show={modalShow}
            title="Send for Approval"
            options={personOptions}
            close={() => setModalShow(false)}
            onSubmit={() => {}}
          />
        </>
      )}
    </div>
  );
};

export default DeliveryChallanList;
